import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionContext';
import { Button } from '../components/ui/button';
import { statsService, apprenantService } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Users, Clock, TrendingUp, Briefcase, LogOut, FileText, Search, AlertCircle, MapPin, FolderOpen, BarChart3, FolderDown, Star, MessageCircle } from 'lucide-react';
import ResponsiveTabs from '../components/ResponsiveTabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import DocumentsPartages from '../components/DocumentsPartages';
import BeneficiairesFinanceur from '../components/BeneficiairesFinanceur';
import SyntheseCollective from '../components/SyntheseCollective';
import ExportManager from '../components/ExportManager';
import QuestionnaireSatisfaction from '../components/QuestionnaireSatisfaction';
import MesOffresEmploi from '../components/MesOffresEmploi';
import Messagerie from '../components/Messagerie';
import MessagerieBadge from '../components/MessagerieBadge';

const FinanceurDashboard = () => {
  const { user, logout } = useAuth();
  const { hasPermission, loading: permLoading } = usePermissions();
  const [stats, setStats] = useState(null);
  const [zone, setZone] = useState(null);
  const [bénéficiaires, setBénéficiaires] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('synthese');

  useEffect(() => {
    // Attendre que les permissions soient chargées avant de charger les données
    if (!permLoading) {
      loadData();
    }
  }, [zone, permLoading]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Toujours charger les stats pour le financeur (il a les permissions par défaut)
      const statsRes = await statsService.getGlobal(zone);
      setStats(statsRes.data);
      
      // Charger les bénéficiaires
      const bénéficiairesRes = await apprenantService.getAll(zone);
      setBénéficiaires(bénéficiairesRes.data || []);
    } catch (error) {
      console.error('Erreur chargement stats:', error);
      toast.error('Erreur de chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePDF = async (bénéficiaireId, bénéficiaireNom) => {
    const now = new Date();
    const mois = now.getMonth() + 1;
    const annee = now.getFullYear();

    try {
      const response = await reportingService.getSyntheseIndividuelle(
        bénéficiaireId,
        mois,
        annee
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `synthese_${bénéficiaireNom}_${mois}_${annee}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF généré avec succès');
    } catch (error) {
      toast.error('Erreur de génération du PDF');
    }
  };

  const filteredBénéficiaires = bénéficiaires.filter(app => 
    `${app.prenom} ${app.nom}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-lg text-slate-600">Chargement...</div>
      </div>
    );
  }

  const contratData = [
    { name: 'CDI', value: stats?.cdi_count || 0, color: '#10B981' },
    { name: 'CDD', value: stats?.cdd_count || 0, color: '#F59E0B' }
  ];

  const freinsData = Object.entries(stats?.freins_statistiques || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([name, value]) => ({ name, value }));

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      {/* Header */}
      <header className="backdrop-blur-xl bg-white/80 border-b border-slate-200 sticky top-0 z-50 py-3 sm:py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 flex flex-wrap justify-between items-center gap-3">
          <div className="min-w-0 flex-1">
            <h1 className="text-lg sm:text-3xl font-bold text-slate-900 truncate" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Dashboard Financeur
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 mt-0.5 sm:mt-1 truncate">
              Bienvenue, {user?.prenom} {user?.nom}
            </p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Sélecteur de zone */}
            <Select value={zone || 'all'} onValueChange={(val) => setZone(val === 'all' ? null : val)}>
              <SelectTrigger data-testid="zone-filter" className="w-32 sm:w-[180px] h-9 rounded-xl">
                <MapPin className="w-4 h-4 mr-1 sm:mr-2 text-slate-500 shrink-0" />
                <SelectValue placeholder="Toutes les zones" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les zones</SelectItem>
                <SelectItem value="Saint-Pierre">Saint-Pierre</SelectItem>
                <SelectItem value="Sainte-Marie">Sainte-Marie</SelectItem>
              </SelectContent>
            </Select>
            {/* Cloche / badge messagerie : compteur de messages non lus */}
            <MessagerieBadge onClick={() => setView('messagerie')} />
            <Button
              onClick={logout}
              data-testid="logout-button"
              variant="outline"
              className="rounded-xl border-slate-200 h-9 px-2 sm:px-3"
            >
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Déconnexion</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation par onglets (responsive) */}
      <nav className="bg-white border-b border-slate-200 px-4 sm:px-6 py-2 sticky top-[65px] sm:top-[73px] z-40">
        <div className="max-w-7xl mx-auto">
          <ResponsiveTabs
            tabs={[
              { value: 'synthese', label: 'Synthèses Collectives', icon: BarChart3 },
              ...(hasPermission('view_apprenants') ? [{ value: 'beneficiaires', label: 'Bénéficiaires', icon: Users }] : []),
              ...(hasPermission('view_documents') ? [{ value: 'documents', label: 'Documents', icon: FileText }] : []),
              ...(hasPermission('view_bourse_emploi') ? [{ value: 'mes-offres', label: 'Mes Offres', icon: FolderOpen }] : []),
              { value: 'exports', label: 'Exports', icon: FolderDown },
              { value: 'satisfaction', label: 'Mon Avis', icon: Star },
              { value: 'messagerie', label: 'Messagerie', icon: MessageCircle },
            ]}
            value={view}
            onValueChange={setView}
            mobilePlaceholder="Choisir une section"
          />
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-4 sm:py-8">
        {view === 'synthese' && (
          <SyntheseCollective zone={zone} />
        )}

        {view === 'beneficiaires' && hasPermission('view_apprenants') && (
          <BeneficiairesFinanceur zone={zone} />
        )}

        {view === 'documents' && hasPermission('view_documents') && (
          <DocumentsPartages userRole="financeur" />
        )}

        {view === 'mes-offres' && hasPermission('view_bourse_emploi') && (
          <MesOffresEmploi />
        )}

        {view === 'exports' && (
          <ExportManager />
        )}

        {view === 'satisfaction' && (
          <QuestionnaireSatisfaction />
        )}

        {view === 'messagerie' && (
          <Messagerie />
        )}

        {view === 'old_stats' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card className="bg-blue-50 border-blue-100 rounded-3xl p-6 shadow-sm hover:-translate-y-1 hover:shadow-md transition-all duration-300">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-bold uppercase tracking-wider text-blue-700">
                      Bénéficiaires Actifs
                    </CardTitle>
                    <Users className="w-8 h-8 text-blue-600" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div data-testid="bénéficiaires-count" className="text-4xl font-extrabold text-blue-900">
                    {stats?.apprenants_actifs || 0}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-amber-50 border-amber-100 rounded-3xl p-6 shadow-sm hover:-translate-y-1 hover:shadow-md transition-all duration-300">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-bold uppercase tracking-wider text-amber-700">
                      Volume Horaire
                    </CardTitle>
                    <Clock className="w-8 h-8 text-amber-600" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div data-testid="total-heures" className="text-4xl font-extrabold text-amber-900">
                    {stats?.total_heures || 0}h
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-emerald-50 border-emerald-100 rounded-3xl p-6 shadow-sm hover:-translate-y-1 hover:shadow-md transition-all duration-300">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-bold uppercase tracking-wider text-emerald-700">
                      Taux de Placement
                    </CardTitle>
                    <TrendingUp className="w-8 h-8 text-emerald-600" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div data-testid="taux-placement" className="text-4xl font-extrabold text-emerald-900">
                    {stats?.taux_placement || 0}%
                  </div>
                  <div className="text-sm text-emerald-700 mt-2">
                    Objectif: 60%
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-rose-50 border-rose-100 rounded-3xl p-6 shadow-sm hover:-translate-y-1 hover:shadow-md transition-all duration-300">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-bold uppercase tracking-wider text-rose-700">
                      Contrats Signés
                    </CardTitle>
                    <Briefcase className="w-8 h-8 text-rose-600" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div data-testid="contrats-count" className="text-4xl font-extrabold text-rose-900">
                    {(stats?.cdi_count || 0) + (stats?.cdd_count || 0)}
                  </div>
                  <div className="text-sm text-rose-700 mt-2">
                    {stats?.cdi_count || 0} CDI • {stats?.cdd_count || 0} CDD
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-white border-slate-200 rounded-3xl p-6 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-xl font-bold" style={{ fontFamily: 'Nunito, sans-serif' }}>
                    Répartition des Contrats
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={contratData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, value }) => `${name}: ${value}`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {contratData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card className="bg-white border-slate-200 rounded-3xl p-6 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
                    <AlertCircle className="w-5 h-5 text-amber-500" />
                    Points de Blocage
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-80 overflow-y-auto">
                    {freinsData.map((frein, index) => {
                      const beneficiaires = stats?.freins_beneficiaires?.[frein.name] || [];
                      return (
                        <div key={frein.name} className="bg-amber-50 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 text-sm font-bold flex items-center justify-center">
                                {index + 1}
                              </span>
                              <span className="font-semibold text-slate-800">{frein.name}</span>
                            </div>
                            <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
                              {frein.value} bénéficiaire{frein.value > 1 ? 's' : ''}
                            </span>
                          </div>
                          {/* Liste des noms des bénéficiaires */}
                          <div className="pl-9 text-sm text-slate-600">
                            {beneficiaires.slice(0, 5).map((b, i) => (
                              <span key={b.id}>
                                {b.prenom} {b.nom}{i < Math.min(beneficiaires.length, 5) - 1 ? ', ' : ''}
                              </span>
                            ))}
                            {beneficiaires.length > 5 && (
                              <span className="text-slate-400"> +{beneficiaires.length - 5} autres</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {freinsData.length === 0 && (
                      <p className="text-slate-500 text-center py-4">Aucun frein identifié</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        )}

        {/* Message si aucune permission */}
        {!hasPermission('view_stats') && !hasPermission('view_apprenants') && !hasPermission('view_documents') && !hasPermission('view_bourse_emploi') && (
          <Card className="bg-amber-50 border-amber-200 rounded-3xl">
            <CardContent className="p-8 text-center">
              <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-amber-800 mb-2">Accès Limité</h3>
              <p className="text-amber-700">
                Vous n'avez actuellement aucune permission configurée pour ce profil.
                Contactez un administrateur pour obtenir les accès nécessaires.
              </p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default FinanceurDashboard;
